/**
 * Provides the scatter plot component.
 *
 * @packageDocumentation
 * @module ScatterPlotComponent
 * @public
 */

import { record as R } from '@highimpact/data-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  upgradeAxisPreferences,
  upgradeToolBarPreferences,
} from '../config/plotPreferences';
import {
  AxisKey,
  AxisPreferences,
  CurveId,
  PlotPreferencesV2,
  RefreshIntervalOption,
  TimeRangeOption,
} from '../config/plotTypes';
import { resolveDisplayTimezone } from '../config/resolveDisplayTimezone';
import { ExportCsvOverlay } from '../export-csv/ExportCsvOverlay';
import { ExportCsvFormValues } from '../export-csv/types';
import { ToolBar } from '../tool-bar/ToolBar';
import {
  buildLayoutStateFromAxisSessionRanges,
  clearAutoscaleAxisRangesInLayoutState,
  clearAutoscaleAxisSessionRanges,
  clearZoomableAxisRangesInLayoutState,
  getAutoscaleAxisKeys,
  mergeTimeseriesValueAxisLayoutState,
  type AxisSessionRanges,
} from './axisSessionLayoutState';
import {
  clearCurveSessionVisibility,
  CurveSessionVisibility,
  CurveSessionVisibilityTraceLike,
  resolveLegendToggleCurveIds,
  shouldResetCurveSessionLegendVisibility,
  toggleCurveSessionVisibility,
} from './curveSessionVisibility';
import Plot, { PlotParams } from './plot';
import { PlotDataInput } from './PlotDataInput';
import {
  buildPlotlyData,
  BuildPlotlyDataOptions,
  buildPlotlyLayout,
  buildPlotlyMargins,
  getPlotlyLayoutIdToAxisKey,
  type BuildPlotlyLayoutOptions,
  type PlotLayoutStateWhitelist,
} from './plotlySettings';
import { computeTimeseriesAutoscaleYRanges } from './timeseriesAutoscaleYRanges';
import {
  computeTimeseriesRelayoutUpdate,
  isHorizontalOnlyTimeseriesRelayout,
} from './timeseriesAxisRelayout';
import {
  resolveTimeseriesAxisLabelMode,
  resolveVisibleTimeseriesRangeMs,
  withTimeseriesTimezoneAnnotation,
} from './timeseriesAxisTicks';

export { buildLayoutStateFromAxisSessionRanges } from './axisSessionLayoutState';
export type { AxisSessionRanges } from './axisSessionLayoutState';
export type { CurveSessionVisibility } from './curveSessionVisibility';

// -- type definitions --

/**
 * Basic props for the scatter plot component.
 */
type BasicPlotComponentProps = PlotPreferencesV2 & {
  data: PlotDataInput;
  type: 'timeseries' | 'scatter';
  onChangeAxisRange?: (
    key: AxisKey,
    range: [number, number] | [string, string] | undefined
  ) => void;
  /** Visible x-axis range (ms since epoch). */
  initialTimeSelection?: [number, number];
  /** Full loaded span for the range slider track; must stay >= visible range. */
  loadedTimeSelection?: [number, number];
  onChangeTimeSelection?: (boundaries: readonly [string, string]) => void;
  /** Callback for when a predefined time range is selected. */
  onToolBarSelectTimeRange?: (timeRangeOption: TimeRangeOption) => void;
  /** Callback for when the reload button is clicked. */
  onToolBarReload?: () => void;
  /** When true, shows the CSV export button on top of the plot. */
  csvExport?: boolean;
  /** Called when the user confirms CSV export. */
  onExportCsv?: (formValues: ExportCsvFormValues) => Promise<void>;
  /** Callback for when the refresh interval is changed. */
  onToolBarRefreshIntervalChange?: (
    refreshIntervalOption: RefreshIntervalOption
  ) => void;
  /** Callback for when the refresh interval is stopped. */
  onToolBarRefreshIntervalStop?: () => void;
  /** Options for the plotly data. */
  plotlyDataOptions?: BuildPlotlyDataOptions;
  /**
   * Session zoom ranges for zoomable value axes (Y and scatter X), keyed by axis key.
   * When provided, the host owns value-axis session state (controlled mode).
   * Prefer {@link useAxisSessionRanges} over manual state in hosts.
   */
  axisSessionRanges?: AxisSessionRanges;
  /**
   * Session legend visibility overrides keyed by curve id.
   * When provided, the host owns legend session state (controlled mode).
   * Prefer {@link useCurveSessionVisibility} over manual state in hosts.
   */
  curveSessionVisibility?: CurveSessionVisibility;
  /** Toggle session legend visibility for one or more curves. */
  onToggleCurveSessionVisibility?: (curveIds: readonly CurveId[]) => void;
  /** Clear all session legend visibility overrides. */
  onResetCurveSessionVisibility?: () => void;
  /**
   * Allowed session layout paths merged into the Plotly layout.
   * Defaults to axis range paths only.
   */
  layoutStateWhitelist?: PlotLayoutStateWhitelist;
};

/**
 * All props for the scatter plot component.
 *
 * A sum type of the {@link BasicPlotComponentProps} and
 * the {@link PlotParams} of the thin wrapper around Plotly.
 */
export type PlotComponentProps = BasicPlotComponentProps &
  Partial<Omit<PlotParams, 'data' | 'layout'>>;

// -- helper functions --

/**
 * Merges a timeseries relayout patch into component layout state.
 */
function mergeTimeseriesRelayoutPatch(
  layoutState: Partial<Plotly.Layout>,
  patch: Partial<Plotly.Layout>
): Partial<Plotly.Layout> {
  return {
    ...layoutState,
    xaxis: {
      ...(layoutState.xaxis ?? {}),
      ...(patch.xaxis ?? {}),
    },
  };
}

/**
 * Extract the range of an axis from a relayout event.
 */
function extractRelayoutAxisRange(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  event: Record<string, any>,
  layoutId: string
): [string, string] | undefined {
  const start = event[`${layoutId}.range[0]`];
  const end = event[`${layoutId}.range[1]`];

  if (start === undefined || end === undefined) {
    return undefined;
  }

  return [`${start}`, `${end}`];
}

/**
 * Parse and validate a numeric axis range.
 */
function parseNumericAxisRange(
  range: [string, string]
): [number, number] | undefined {
  const start = Number(range[0]);
  const end = Number(range[1]);

  if (Number.isNaN(start) || Number.isNaN(end)) {
    return undefined;
  }

  return [start, end];
}

/**
 * Set the range of an axis in the layout state.
 */
function setLayoutAxisRange(
  layoutState: Partial<Plotly.Layout>,
  layoutId: string,
  range: [number, number] | [string, string]
): Partial<Plotly.Layout> {
  const existing = layoutState[layoutId as keyof Plotly.Layout] as
    | Record<string, unknown>
    | undefined;

  return {
    ...layoutState,
    [layoutId]: {
      ...(existing ?? {}),
      range,
    },
  };
}

/**
 * Clear the range of an axis in the layout state.
 */
function clearLayoutAxisRange(
  layoutState: Partial<Plotly.Layout>,
  layoutId: string
): Partial<Plotly.Layout> {
  const existing = layoutState[layoutId as keyof Plotly.Layout] as
    | { range?: unknown }
    | undefined;

  if (!existing?.range) {
    return layoutState;
  }

  const { range: _range, ...rest } = existing;

  if (Object.keys(rest).length === 0) {
    const next = { ...layoutState };
    delete next[layoutId as keyof Plotly.Layout];
    return next;
  }

  return {
    ...layoutState,
    [layoutId]: rest,
  };
}

/**
 * Prune the layout state for disabled axis zoom.
 *
 * Uses the {@link clearLayoutAxisRange} function.
 */
function pruneLayoutStateForDisabledAxisZoom(
  layoutState: Partial<Plotly.Layout>,
  layoutIdToAxisKey: Record<string, AxisKey>,
  axisPrefs: Record<AxisKey, AxisPreferences>
): Partial<Plotly.Layout> {
  let next = layoutState;

  Object.entries(layoutIdToAxisKey).forEach(([layoutId, axisKey]) => {
    const axis = axisPrefs[axisKey];
    if (!axis) {
      return;
    }

    if (upgradeAxisPreferences(axis).zoomEnabled === false) {
      next = clearLayoutAxisRange(next, layoutId);
    }
  });

  return next;
}

export const ScatterPlotComponent: React.FC<PlotComponentProps> = ({
  data,
  type,
  curvePrefs,
  curveOrder,
  curveToTimezone,
  timeseriesAxisLabelMode,
  axisPrefs,
  dataSourceToAxis,
  yAxisOrder,
  showRangeSlider,
  curveRelations,
  thresholdPrefs,
  toolBarPrefs,
  onChangeAxisRange,
  axisSessionRanges,
  curveSessionVisibility,
  onToggleCurveSessionVisibility,
  onResetCurveSessionVisibility,
  layoutStateWhitelist,
  initialTimeSelection,
  loadedTimeSelection,
  onChangeTimeSelection,
  onToolBarSelectTimeRange,
  onToolBarReload,
  onToolBarRefreshIntervalChange,
  onToolBarRefreshIntervalStop,
  plotlyDataOptions,
  csvExport,
  onExportCsv,
  onDoubleClick: onPlotDoubleClick,
  onLegendClick: onPlotLegendClick,
  onLegendDoubleClick: onPlotLegendDoubleClick,
  ...plotProps
}) => {
  // -- preferences --

  const resolvedToolBarPrefs = useMemo(
    () => upgradeToolBarPreferences(toolBarPrefs),
    [toolBarPrefs]
  );

  const prefs: PlotPreferencesV2 = useMemo(
    () => ({
      version: 2,
      curvePrefs,
      curveOrder,
      curveToTimezone,
      timeseriesAxisLabelMode,
      axisPrefs,
      dataSourceToAxis,
      yAxisOrder,
      showRangeSlider,
      curveRelations,
      thresholdPrefs,
      toolBarPrefs: resolvedToolBarPrefs,
    }),
    [
      axisPrefs,
      curvePrefs,
      curveOrder,
      curveToTimezone,
      timeseriesAxisLabelMode,
      curveRelations,
      dataSourceToAxis,
      resolvedToolBarPrefs,
      showRangeSlider,
      thresholdPrefs,
      yAxisOrder,
    ]
  );

  const displayTimezone = useMemo(
    () => resolveDisplayTimezone(prefs, type),
    [prefs, type]
  );

  const resolvedTimeseriesAxisLabelMode = useMemo(
    () => resolveTimeseriesAxisLabelMode(timeseriesAxisLabelMode),
    [timeseriesAxisLabelMode]
  );

  // -- layout state --

  const [layoutState, setLayoutState] = useState<Partial<Plotly.Layout>>(
    initialTimeSelection && type === 'timeseries'
      ? { xaxis: { range: initialTimeSelection } }
      : {}
  );

  /**
   * Ensures autoscale y ranges refresh immediately on horizontal relayout before
   * the host session state catches up in controlled mode.
   */
  const pendingHorizontalAutoscaleRefreshRef = useRef(false);

  const [internalCurveSessionVisibility, setInternalCurveSessionVisibility] =
    useState<CurveSessionVisibility>({});

  const isCurveSessionControlled = curveSessionVisibility !== undefined;

  const effectiveCurveSessionVisibility = isCurveSessionControlled
    ? curveSessionVisibility
    : internalCurveSessionVisibility;

  const sortedDataEpochMs = useMemo(
    () =>
      Array.from(data.keys())
        .map((instant) => instant.getTime())
        .sort((a, b) => a - b),
    [data]
  );
  const firstDateInData = sortedDataEpochMs[0];
  const lastDateInData = sortedDataEpochMs[sortedDataEpochMs.length - 1];

  const visibleXRangeMs = useMemo(() => {
    const fromLayout = resolveVisibleTimeseriesRangeMs(
      layoutState.xaxis?.range,
      initialTimeSelection
    );
    if (fromLayout) {
      return fromLayout;
    }

    if (loadedTimeSelection) {
      return loadedTimeSelection;
    }

    if (
      type === 'timeseries' &&
      firstDateInData !== undefined &&
      lastDateInData !== undefined &&
      firstDateInData < lastDateInData
    ) {
      return [firstDateInData, lastDateInData] as const;
    }

    return undefined;
  }, [
    firstDateInData,
    initialTimeSelection,
    lastDateInData,
    layoutState.xaxis?.range,
    loadedTimeSelection,
    type,
  ]);

  const plotlyDataOptionsWithTimezone = useMemo(
    () => ({
      ...plotlyDataOptions,
      displayTimezone,
      timeseriesAxisLabelMode: resolvedTimeseriesAxisLabelMode,
      visibleXRangeMs,
      curveSessionVisibility: effectiveCurveSessionVisibility,
    }),
    [
      displayTimezone,
      effectiveCurveSessionVisibility,
      plotlyDataOptions,
      resolvedTimeseriesAxisLabelMode,
      visibleXRangeMs,
    ]
  );

  const plotlyLayoutOptions: BuildPlotlyLayoutOptions = useMemo(
    () => ({
      displayTimezone,
      timeseriesAxisLabelMode: resolvedTimeseriesAxisLabelMode,
      visibleXRangeMs,
    }),
    [displayTimezone, resolvedTimeseriesAxisLabelMode, visibleXRangeMs]
  );

  // -- plot data --

  const useWebGL = !showRangeSlider; // range slider does not render well with WebGL
  const plotData = useMemo(
    () =>
      buildPlotlyData(
        prefs,
        data,
        type,
        useWebGL,
        plotlyDataOptionsWithTimezone
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      prefs,
      data,
      type,
      useWebGL,
      plotlyDataOptionsWithTimezone,
      firstDateInData,
      lastDateInData,
    ]
  );

  // -- layout --

  const plotlyLayoutIdToAxisKey = useMemo(
    () => getPlotlyLayoutIdToAxisKey(prefs, type),
    [prefs, type]
  );

  const yLayoutIds = useMemo(
    () =>
      Object.keys(plotlyLayoutIdToAxisKey).filter((layoutId) =>
        layoutId.startsWith('yaxis')
      ),
    [plotlyLayoutIdToAxisKey]
  );

  const autoscaleYRanges = useMemo(() => {
    if (type !== 'timeseries' || !visibleXRangeMs) {
      return {};
    }

    return computeTimeseriesAutoscaleYRanges({
      preferences: prefs,
      data,
      visibleXRangeMs,
      curveSessionVisibility: effectiveCurveSessionVisibility,
    });
  }, [data, effectiveCurveSessionVisibility, prefs, type, visibleXRangeMs]);

  const isAxisSessionControlled = axisSessionRanges !== undefined;

  const effectiveAxisSessionRanges = useMemo(() => {
    if (!isAxisSessionControlled || !axisSessionRanges) {
      return axisSessionRanges;
    }

    if (!pendingHorizontalAutoscaleRefreshRef.current) {
      return axisSessionRanges;
    }

    return clearAutoscaleAxisSessionRanges(axisSessionRanges, axisPrefs);
  }, [
    axisPrefs,
    axisSessionRanges,
    isAxisSessionControlled,
    layoutState.xaxis?.range,
  ]);

  const sessionLayoutState = useMemo(() => {
    if (type === 'timeseries') {
      return mergeTimeseriesValueAxisLayoutState({
        layoutState,
        axisPrefs,
        layoutIdToAxisKey: plotlyLayoutIdToAxisKey,
        axisSessionRanges: isAxisSessionControlled
          ? effectiveAxisSessionRanges
          : undefined,
        autoscaleYRanges,
      });
    }

    if (!isAxisSessionControlled) {
      return layoutState;
    }

    const valueAxisState = buildLayoutStateFromAxisSessionRanges(
      axisSessionRanges,
      plotlyLayoutIdToAxisKey
    );

    return {
      ...layoutState,
      ...valueAxisState,
    };
  }, [
    autoscaleYRanges,
    axisPrefs,
    axisSessionRanges,
    effectiveAxisSessionRanges,
    isAxisSessionControlled,
    layoutState,
    plotlyLayoutIdToAxisKey,
    type,
  ]);

  const xAxisRange = Array.isArray(layoutState.xaxis?.range)
    ? layoutState.xaxis?.range
    : undefined;
  const startRange = xAxisRange?.[0];
  const endRange = xAxisRange?.[1];
  const layout = useMemo(() => {
    const base = {
      ...buildPlotlyLayout(
        prefs,
        type,
        sessionLayoutState,
        layoutStateWhitelist,
        plotlyLayoutOptions
      ),
      autosize: true,
      margin: buildPlotlyMargins(prefs, type),
      legend: {
        xanchor: 'left',
        yanchor: 'bottom',
        x: 0,
        y: 1,
        orientation: 'h' as const,
      },
    } as Partial<Plotly.Layout>;

    const withTimezoneLabel = withTimeseriesTimezoneAnnotation(
      base,
      displayTimezone,
      type,
      showRangeSlider
    );

    if (
      type === 'timeseries' &&
      showRangeSlider &&
      loadedTimeSelection &&
      withTimezoneLabel.xaxis
    ) {
      // Rangeslider track uses epoch ms directly (no timezone shift).
      return {
        ...withTimezoneLabel,
        xaxis: {
          ...withTimezoneLabel.xaxis,
          rangeslider: {
            ...(withTimezoneLabel.xaxis.rangeslider ?? {}),
            range: loadedTimeSelection,
          },
        },
      };
    }

    return withTimezoneLabel;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prefs,
    type,
    startRange,
    endRange,
    loadedTimeSelection,
    showRangeSlider,
    sessionLayoutState,
    layoutStateWhitelist,
    displayTimezone,
    plotlyLayoutOptions,
  ]);

  // -- layout state management --

  useEffect(() => {
    if (isAxisSessionControlled) {
      return;
    }

    setLayoutState((prev) =>
      pruneLayoutStateForDisabledAxisZoom(
        prev,
        plotlyLayoutIdToAxisKey,
        axisPrefs
      )
    );
  }, [axisPrefs, isAxisSessionControlled, plotlyLayoutIdToAxisKey]);

  // if initialTimeSelection changes and is defined, overwrite the internal memory of slider range
  useEffect(() => {
    if (initialTimeSelection && type === 'timeseries') {
      setLayoutState((prev) => {
        const currentRange = prev.xaxis?.range;
        const nextRange = initialTimeSelection;
        const hasSameRange =
          Array.isArray(currentRange) &&
          currentRange.length === 2 &&
          `${currentRange[0]}` === `${nextRange[0]}` &&
          `${currentRange[1]}` === `${nextRange[1]}`;

        if (hasSameRange) {
          return prev;
        }

        return setLayoutAxisRange(prev, 'xaxis', nextRange);
      });
    }
  }, [initialTimeSelection, type]);

  useEffect(() => {
    if (!pendingHorizontalAutoscaleRefreshRef.current) {
      return;
    }

    const stillHasAutoscaleSession = getAutoscaleAxisKeys(axisPrefs).some(
      (axisKey) => axisSessionRanges?.[axisKey] !== undefined
    );

    if (!stillHasAutoscaleSession) {
      pendingHorizontalAutoscaleRefreshRef.current = false;
    }
  }, [axisPrefs, axisSessionRanges]);

  // -- event handlers --

  /**
   * Persist the range of a zoomable y or scatter x axis.
   *
   * @param layoutId - The id of the layout.
   * @param axisKey - The key of the axis.
   * @param rawRange - The raw range.
   */
  const persistZoomableAxisRange = useCallback(
    (layoutId: string, axisKey: AxisKey, rawRange: [string, string]) => {
      const axis = axisPrefs[axisKey];
      if (!axis || upgradeAxisPreferences(axis).zoomEnabled === false) {
        return;
      }

      const isScatterX = layoutId === 'xaxis' && type === 'scatter';
      const isYAxis = layoutId.startsWith('yaxis');

      if (!isScatterX && !isYAxis) {
        return;
      }

      const numericRange = parseNumericAxisRange(rawRange);
      if (!numericRange) {
        return;
      }

      if (isAxisSessionControlled) {
        onChangeAxisRange?.(axisKey, numericRange);
        return;
      }

      setLayoutState((prev) =>
        setLayoutAxisRange(prev, layoutId, numericRange)
      );
      onChangeAxisRange?.(axisKey, numericRange);
    },
    [axisPrefs, isAxisSessionControlled, onChangeAxisRange, type]
  );

  /**
   * Time of last relayout event for debouncing slider range change events.
   */
  const debounceRef = useRef<number | null>(null);

  /** Tracks the previous legend click to detect double-clicks when Plotly omits the double-click event. */
  const lastLegendClickRef = useRef<{
    curveNumber: number;
    timestamp: number;
  } | null>(null);

  /** Ignores stray legend clicks immediately after a double-click reset. */
  const suppressLegendClickUntilRef = useRef(0);

  /** Defers single legend clicks so double-click can cancel them and restore all traces. */
  const legendClickDebounceRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (legendClickDebounceRef.current) {
        clearTimeout(legendClickDebounceRef.current);
      }
    };
  }, []);

  /**
   * Handle the relayout event for time selection slider and zoomable axes.
   *
   * Uses a debouncer to prevent repeated events from firing the callback multiple times.
   * @see https://developer.mozilla.org/en-US/docs/Glossary/Debounce
   */
  const handleRelayout = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (event: any) => {
      // clean up: clear debouncer timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      const horizontalOnlyRelayout =
        type === 'timeseries' &&
        isHorizontalOnlyTimeseriesRelayout(event, yLayoutIds);

      if (horizontalOnlyRelayout) {
        pendingHorizontalAutoscaleRefreshRef.current = true;

        if (isAxisSessionControlled) {
          getAutoscaleAxisKeys(axisPrefs).forEach((axisKey) => {
            onChangeAxisRange?.(axisKey, undefined);
          });
        } else {
          setLayoutState((prev) =>
            clearAutoscaleAxisRangesInLayoutState(
              prev,
              axisPrefs,
              plotlyLayoutIdToAxisKey
            )
          );
        }
      }

      // zoomable value/scatter axes (Y and scatter X)
      R.keys(plotlyLayoutIdToAxisKey).forEach((layoutId) => {
        const isX = layoutId.startsWith('x');
        const rawRange = extractRelayoutAxisRange(event, layoutId);

        if (!rawRange) {
          return;
        }

        if (!isX || type === 'scatter') {
          const axisKey = plotlyLayoutIdToAxisKey[layoutId];
          if (axisKey) {
            persistZoomableAxisRange(layoutId, axisKey, rawRange);
          }
        }
      });
      // time axis: numeric relayout (epoch ms)
      if (type === 'timeseries') {
        try {
          const relayoutUpdate = computeTimeseriesRelayoutUpdate({
            event,
            displayTimezone,
            labelMode: resolvedTimeseriesAxisLabelMode,
          });

          setLayoutState((prev) =>
            mergeTimeseriesRelayoutPatch(prev, relayoutUpdate.layoutStatePatch)
          );
        } catch {
          // Ignore relayout events that do not include a numeric x-axis range.
        }
      }

      debounceRef.current = window.setTimeout(() => {
        if (type !== 'timeseries') {
          return;
        }

        try {
          const relayoutUpdate = computeTimeseriesRelayoutUpdate({
            event,
            displayTimezone,
            labelMode: resolvedTimeseriesAxisLabelMode,
          });
          onChangeTimeSelection?.(relayoutUpdate.isoRange);
        } catch {
          // Ignore relayout events that do not include a numeric x-axis range.
        }
      }, 200);
    },
    [
      axisPrefs,
      displayTimezone,
      isAxisSessionControlled,
      onChangeAxisRange,
      onChangeTimeSelection,
      persistZoomableAxisRange,
      plotlyLayoutIdToAxisKey,
      resolvedTimeseriesAxisLabelMode,
      type,
      yLayoutIds,
    ]
  );

  const handleDoubleClick = useCallback(() => {
    if (!isAxisSessionControlled) {
      setLayoutState((prev) =>
        clearZoomableAxisRangesInLayoutState(
          prev,
          axisPrefs,
          plotlyLayoutIdToAxisKey,
          type
        )
      );
    }

    onPlotDoubleClick?.();
  }, [
    axisPrefs,
    isAxisSessionControlled,
    onPlotDoubleClick,
    plotlyLayoutIdToAxisKey,
    type,
  ]);

  const updateCurveSessionVisibility = useCallback(
    (curveIds: readonly CurveId[]) => {
      if (curveIds.length === 0) {
        return;
      }

      if (isCurveSessionControlled) {
        onToggleCurveSessionVisibility?.(curveIds);
        return;
      }

      setInternalCurveSessionVisibility((previous) =>
        toggleCurveSessionVisibility(previous, curveIds)
      );
    },
    [isCurveSessionControlled, onToggleCurveSessionVisibility]
  );

  const resetCurveSessionVisibilityState = useCallback(() => {
    if (isCurveSessionControlled) {
      onResetCurveSessionVisibility?.();
      return;
    }

    setInternalCurveSessionVisibility(clearCurveSessionVisibility());
  }, [isCurveSessionControlled, onResetCurveSessionVisibility]);

  const cancelPendingLegendToggle = useCallback(() => {
    if (legendClickDebounceRef.current) {
      clearTimeout(legendClickDebounceRef.current);
      legendClickDebounceRef.current = null;
    }
  }, []);

  const handleLegendDoubleClickReset = useCallback(() => {
    cancelPendingLegendToggle();
    lastLegendClickRef.current = null;
    suppressLegendClickUntilRef.current = Date.now() + 300;
    resetCurveSessionVisibilityState();
  }, [cancelPendingLegendToggle, resetCurveSessionVisibilityState]);

  const handleLegendClick = useCallback(
    (event: Readonly<Plotly.LegendClickEvent>): boolean => {
      const eventTrace = event.fullData ?? event.data;
      const suppressed = Date.now() < suppressLegendClickUntilRef.current;
      const curveIds = resolveLegendToggleCurveIds(
        plotData as CurveSessionVisibilityTraceLike[],
        event.curveNumber,
        eventTrace as CurveSessionVisibilityTraceLike
      );
      const now = Date.now();
      const lastClick = lastLegendClickRef.current;
      const isDoubleClickCandidate =
        lastClick !== null &&
        lastClick.curveNumber === event.curveNumber &&
        now - lastClick.timestamp < 300;

      if (suppressed) {
        return false;
      }

      if (isDoubleClickCandidate) {
        handleLegendDoubleClickReset();
        return true;
      }

      lastLegendClickRef.current = {
        curveNumber: event.curveNumber,
        timestamp: now,
      };

      if (curveIds.length === 0) {
        return onPlotLegendClick?.(event) ?? true;
      }

      cancelPendingLegendToggle();
      legendClickDebounceRef.current = window.setTimeout(() => {
        updateCurveSessionVisibility(curveIds);
        legendClickDebounceRef.current = null;
        lastLegendClickRef.current = null;
      }, 300);
      return false;
    },
    [
      cancelPendingLegendToggle,
      handleLegendDoubleClickReset,
      onPlotLegendClick,
      plotData,
      updateCurveSessionVisibility,
    ]
  );

  const handleLegendDoubleClick = useCallback(
    (event: Readonly<Plotly.LegendClickEvent>): boolean => {
      const eventTrace = event.fullData ?? event.data;
      const shouldResetSession = shouldResetCurveSessionLegendVisibility(
        effectiveCurveSessionVisibility,
        plotData as CurveSessionVisibilityTraceLike[]
      );
      const curveIds = resolveLegendToggleCurveIds(
        plotData as CurveSessionVisibilityTraceLike[],
        event.curveNumber,
        eventTrace as CurveSessionVisibilityTraceLike
      );

      cancelPendingLegendToggle();

      if (!shouldResetSession && curveIds.length === 0) {
        return onPlotLegendDoubleClick?.(event) ?? true;
      }

      handleLegendDoubleClickReset();
      return shouldResetSession;
    },
    [
      cancelPendingLegendToggle,
      effectiveCurveSessionVisibility,
      handleLegendDoubleClickReset,
      onPlotLegendDoubleClick,
      plotData,
    ]
  );

  // -- render --

  return (
    <div
      style={{
        minHeight: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{ position: 'relative', width: '100%', flex: 1, minHeight: 0 }}
      >
        {csvExport && (
          <ExportCsvOverlay
            curvePrefs={prefs.curvePrefs}
            timeRanges={{
              loaded: loadedTimeSelection,
              visible: visibleXRangeMs,
            }}
            onExportCsv={onExportCsv}
          />
        )}
        {/* Plotly plot */}
        <Plot
          {...plotProps}
          data={plotData}
          layout={layout as Partial<Plotly.Layout>}
          style={{ width: '100%', height: '100%', ...(plotProps.style ?? {}) }}
          onDoubleClick={handleDoubleClick}
          onLegendClick={handleLegendClick}
          onLegendDoubleClick={handleLegendDoubleClick}
          onRelayout={(e) => {
            handleRelayout(e);
            plotProps.onRelayout?.(e);
          }}
          useResizeHandler
        />
      </div>
      {/* Tool bar */}
      <ToolBar
        hide={resolvedToolBarPrefs.hide}
        useTimeRangePills={resolvedToolBarPrefs.useTimeRangePills}
        useReloadButton={resolvedToolBarPrefs.useReloadButton}
        useRefreshIntervalSelector={
          resolvedToolBarPrefs.useRefreshIntervalSelector
        }
        value={resolvedToolBarPrefs.selectedRefreshInterval}
        options={resolvedToolBarPrefs.refreshIntervalOptions}
        units={resolvedToolBarPrefs.timeRangeOptions}
        initialSelected={null}
        onSelect={(timeRangeOption) =>
          onToolBarSelectTimeRange?.(timeRangeOption)
        }
        onReload={() => onToolBarReload?.()}
        onIntervalChange={(refreshIntervalOption) =>
          onToolBarRefreshIntervalChange?.(refreshIntervalOption)
        }
        onStop={() => onToolBarRefreshIntervalStop?.()}
      />
    </div>
  );
};
